import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  address?: string;
  notes?: string;
  status: string;
  offer_price?: number;
  offer_date?: string;
  created_at: string;
  updated_at: string;
}

interface UseContactFormProps {
  contact: Contact | null;
  onSuccess: () => void;
}

export const useContactForm = ({ contact, onSuccess }: UseContactFormProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    notes: "",
    status: "active",
    offer_price: "",
    offer_date: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        company: contact.company || "",
        address: contact.address || "",
        notes: contact.notes || "",
        status: contact.status || "active",
        offer_price: contact.offer_price?.toString() || "",
        offer_date: contact.offer_date || "",
      });
    }
  }, [contact]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data with proper type conversion
      const submitData = {
        ...formData,
        offer_price: formData.offer_price ? parseFloat(formData.offer_price) : null,
        offer_date: formData.offer_date || null,
      };

      if (contact) {
        const { error } = await supabase
          .from('customers')
          .update(submitData)
          .eq('id', contact.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Contact updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('customers')
          .insert(submitData);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Contact created successfully",
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast({
        title: "Error",
        description: "Failed to save contact",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    handleInputChange,
    handleSubmit,
  };
};